import { FormContainer } from '@strata/react-form-container';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { PayerMarketMoveSegmentsDialog } from 'pages/payerManagement/dialogs/PayerMarketMoveSegmentsDialog';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import { getPayerSegmentMap } from 'services/PayerSegmentSupport';

/** Determine the segments that will be moved from other payer markets
 * and the payer markets that will be deleted as a result of the payer market creation/update */
const determineMovedSegments = ({ payerMarketId, payerMarkets, segmentIds, payerSegmentMap }) => {
    // Filter out segment ids that are part of existing payer market
    const segmentIdsToMove = segmentIds.filter(
        (segmentId) =>
            !payerMarkets
                .find((market) => market.payer_market_id === payerMarketId)
                ?.segment_ids.includes(segmentId),
    );

    const payerMarketsToDelete = payerMarkets
        .filter(
            (market) =>
                // Filter out current market & any markets that will still have segments
                market.payer_market_id !== payerMarketId &&
                market.segment_ids.every((id) => segmentIdsToMove.includes(id)),
        )
        .map((market) => ({
            payerMarketName: market.payer_market_name,
            removedSegmentNames: market.segment_ids.map(
                (segmentId) => payerSegmentMap?.[segmentId]?.segment_name ?? segmentId,
            ),
        }));

    const segmentsToMove = segmentIdsToMove.map(
        (segmentId) => payerSegmentMap?.[segmentId]?.segment_name ?? segmentId,
    );

    return { segmentsToMove, payerMarketsToDelete };
};

export const PayerMarketDetails = () => {
    const { payerMarketId } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate('/payer-market');

    const isCreate = payerMarketId === CREATE_ROUTE;

    const payerMarket = useGet(!isCreate ? `/reference/payer_markets/${payerMarketId}` : null);
    const payerMarkets = useGet('/reference/payer_markets');

    const payers = useGet('/reference/payers/');
    const payerOptions = (payers ?? []).map((payer) => ({
        name: payer.payer_name,
        secondaryData: payer.payer_id,
    }));

    const payerSegments = useGet('/reference/payer_segments');
    const payerSegmentMap = getPayerSegmentMap(payerSegments);
    const segmentOptions = (payerSegments ?? []).map((segment) => ({
        name: segment.segment_name,
        secondaryData: segment.segment_id,
        payerId: segment.payer_id,
    }));

    const [segmentsToMove, setSegmentsToMove] = useState([]);
    const [payerMarketsToDelete, setPayerMarketsToDelete] = useState([]);

    const submitForm = async (values) => {
        try {
            if (isCreate) {
                await post('/reference/payer_markets', {
                    payer_market_name: values.payerMarketName,
                    segment_ids: values.segmentIds,
                    payer_id: values.payerId,
                });
            } else {
                await put(`/reference/payer_markets/${payerMarketId}`, {
                    payer_market_name: values.payerMarketName,
                    segment_ids: values.segmentIds,
                });
            }

            await SuccessToast({
                message: `Payer Market ${isCreate ? 'created' : 'updated'} successfully.`,
            });
            goHome();
        } catch (err) {
            handleError('Error saving payer market.', err);
        }
    };

    return (
        <Formik
            initialValues={{
                payerMarketName: payerMarket?.payer_market_name,
                payerId: payerMarket?.payer_id,
                segmentIds: payerMarket?.segment_ids,
            }}
            enableReinitialize
            validationSchema={Yup.object({
                payerMarketName: Yup.string()
                    .max(100, validationStrings.maxCharacter[100])
                    .required(validationStrings.requiredField.errorMessage),
                payerId: Yup.string().required(validationStrings.requiredField.errorMessage),
                segmentIds: Yup.array(Yup.string().required())
                    .required(validationStrings.requiredField.errorMessage)
                    .min(1, validationStrings.requiredField.errorMessage),
            })}
            onSubmit={async (values) => {
                const movedSegmentResult = determineMovedSegments({
                    payerMarketId,
                    payerMarkets,
                    payerSegmentMap,
                    segmentIds: values.segmentIds,
                });

                if (movedSegmentResult.segmentsToMove.length > 0) {
                    setSegmentsToMove(movedSegmentResult.segmentsToMove);
                    setPayerMarketsToDelete(movedSegmentResult.payerMarketsToDelete);
                } else {
                    await submitForm(values);
                }
            }}
        >
            {(formik) => (
                <>
                    <PayerMarketMoveSegmentsDialog
                        segmentsToMove={segmentsToMove}
                        payerMarketsToDelete={payerMarketsToDelete}
                        onClose={() => {
                            setSegmentsToMove([]);
                            setPayerMarketsToDelete([]);
                        }}
                        onSubmit={async () => {
                            await submitForm(formik.values);
                        }}
                    />
                    <FormContainer
                        formTitle={`${isCreate ? 'Create' : 'Edit'} Payer Market`}
                        onFormSubmit={formik.handleSubmit}
                        onSecondaryButtonClick={goHome}
                        isLoading={!(isCreate || payerMarket) || !payers}
                        isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                        formLeftContent={
                            <>
                                <TextInputFormik
                                    id="payerMarketName"
                                    label="Payer Market Name"
                                    required
                                />
                                <SuggestSingleSelectFormik
                                    id="payerId"
                                    label="Payer"
                                    required
                                    disabled={!isCreate}
                                    values={payerOptions}
                                    selectedItem={payerOptions?.find(
                                        ({ secondaryData }) =>
                                            secondaryData === formik.values.payerId,
                                    )}
                                    onChange={(newValue) => {
                                        void (async () => {
                                            await formik.setFieldValue(
                                                'payerId',
                                                newValue?.secondaryData,
                                            );
                                            await formik.setFieldValue('segmentIds', undefined);
                                        })();
                                    }}
                                />
                                <SuggestMultiSelectFormik
                                    id="segmentIds"
                                    label="Payer Segments"
                                    required
                                    disabled={!formik.values.payerId}
                                    values={segmentOptions?.filter(
                                        ({ payerId }) => payerId === formik.values.payerId,
                                    )}
                                    selectedItems={segmentOptions?.filter(({ secondaryData }) =>
                                        formik.values.segmentIds?.includes(secondaryData),
                                    )}
                                    onChange={(newValues) =>
                                        formik.setFieldValue(
                                            'segmentIds',
                                            newValues?.map(({ secondaryData }) => secondaryData),
                                        )
                                    }
                                />
                            </>
                        }
                    />
                </>
            )}
        </Formik>
    );
};
